import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  Slide,
  TextField,
  Divider,
  Button,
  Box,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import * as taskTemplateStyle from "../fieldmap/tasktemplates/TaskTemplateStyle";
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const initialHeight = 300;
const halfHeight = window.innerHeight * 0.5;
const maxHeight = window.innerHeight * 0.75;
const BottomSheetNestedDropdown = ({
  label,
  bottomSheetTitleText,
  options = [],
  onChange,
  defaultValue = { element: null, subElement: null, leaves: [] },
  error = false,
  helperText = "",
  required = false,
  bottomSheetHeight = "50vh"
}) => {
  const [selectedItem, setSelectedItem] = useState(defaultValue);
  const [path, setPath] = useState([]);
  const [localSelectedLeaves, setLocalSelectedLeaves] = useState([]);
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [dialogTitle, setDialogTitle] = useState(bottomSheetTitleText || label || "");
  const [internalError, setInternalError] = useState(false);
  const [height, setHeight] = useState(initialHeight);
  const [startY, setStartY] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(initialHeight);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (
      defaultValue &&
      JSON.stringify(defaultValue) !== JSON.stringify(selectedItem)
    ) {
      setSelectedItem(defaultValue);
      if (defaultValue.leaves?.length > 0 || defaultValue.element) {
        setInternalError(false);
      }
    }
  }, [defaultValue, selectedItem]);
  useEffect(() => {
    if (open) {
      if (path.length > 0) {
        setDialogTitle(path[path.length - 1]);
      } else {
        setDialogTitle(bottomSheetTitleText || label || "");
      }
    } else {
      setDialogTitle(bottomSheetTitleText || label || "");
    }
  }, [open, label, path, bottomSheetTitleText]);

  const handleOpen = () => {
    setOpen(true);
    setHasOpened(true);
    setHeight(initialHeight);
    // Always reset path to root (top-level elements) when opening
    setPath([]);
    setLocalSelectedLeaves([]); // Clear any previous local selections
  };

  const handleClose = () => {
    if (required && (!selectedItem.element || selectedItem.leaves?.length === 0)) {
      setInternalError(true);
    }
    setOpen(false);
    setPath([]);
    setDialogTitle(bottomSheetTitleText || label || "");
  };
  const getCurrentLevelData = () => {
    let currentLevel = options;
    let currentElement = null;
    let currentSubElement = null;
    for (let i = 0; i < path.length; i++) {
      const segment = path[i];
      let found = false;
      for (const item of currentLevel) {
        if (item.element === segment) {
          if (i === 0) currentElement = item.element;
          if (i === 1) currentSubElement = item.element;
          currentLevel = item.elements || item.leaves;
          found = true;
          break;
        }
      }
      if (!found) {
        return { items: [], element: null, subElement: null };
      }
    }
    if (path.length > 0) {
      const lastSegment = path[path.length - 1];
      let parentOfLeaves = null;
      if (path.length === 1) {
        parentOfLeaves = options.find((opt) => opt.element === lastSegment);
        currentElement = lastSegment;
        currentSubElement = null;
      } else if (path.length === 2) {
        const topParent = options.find((opt) => opt.element === path[0]);
        parentOfLeaves = topParent?.elements?.find((subOpt) => subOpt.element === lastSegment);
        currentElement = path[0];
        currentSubElement = lastSegment;
      }
      if (parentOfLeaves && parentOfLeaves.leaves) {
        return {
          items: parentOfLeaves.leaves.map((leaf) => ({ leaf })),
          element: currentElement,
          subElement: currentSubElement,
        };
      }
    }
    const itemsToDisplay = currentLevel.filter((item) => item.element !== undefined);
    return { items: itemsToDisplay, element: currentElement, subElement: currentSubElement };
  };
  const { items: currentDisplayItems, element: currentContextElement, subElement: currentContextSubElement } = getCurrentLevelData();
  const getSelectedItemDisplay = () => {
    const { element, subElement, leaves } = selectedItem;
    if (!element && (!leaves || leaves.length === 0)) return "";
    if (element && leaves?.length === 0) return element;
    if (element && subElement && leaves?.length > 0) {
      return element === subElement
        ? `${element}: ${leaves.join(", ")}`
        : `${element}: ${subElement}: ${leaves.join(", ")}`;
    }
    if (element && !subElement && leaves?.length > 0) {
      return `${element}: ${leaves.join(", ")}`;
    }
    return "";
  };
  const handleItemClick = (item) => {
    const hasChildren = item.elements?.length > 0 || item.leaves?.length > 0;
    const isLeaf = item.leaf !== undefined;
    if (isLeaf) {
      setLocalSelectedLeaves((prev) =>
        prev.includes(item.leaf)
          ? prev.filter((l) => l !== item.leaf)
          : [...prev, item.leaf]
      );
    } else if (hasChildren) {
      const newPath = [...path, item.element];
      setPath(newPath);

      // When navigating to a new level, check if it's the previously selected branch
      // Only initialize localSelectedLeaves if we are on a leaf selection screen AND the path matches
      const isNavigatingToLeafScreen = item.leaves?.length > 0;
      const matchesConfirmedSelectionBranch =
        selectedItem.element === newPath[0] &&
        ((newPath.length === 1 && !selectedItem.subElement && !item.elements) || // First level, if selected element is the only element
          (newPath.length === 2 && selectedItem.subElement === newPath[1])); // Second level matches subElement

      if (isNavigatingToLeafScreen && matchesConfirmedSelectionBranch) {
        setLocalSelectedLeaves([...selectedItem.leaves]);
      } else {
        setLocalSelectedLeaves([]);
      }
    }
  };
  const handleOk = () => {
    const newSelectedItem = {
      element: currentContextElement,
      subElement: currentContextSubElement,
      leaves: localSelectedLeaves,
    };
    setSelectedItem(newSelectedItem);
    setInternalError(false);
    if (onChange) {
      onChange(newSelectedItem);
    }
    handleClose();
  };
  const handleBack = () => {
    setPath((prev) => {
      const newPath = prev.slice(0, -1);
      // When going back, if the new path represents the previously selected branch,
      // re-evaluate localSelectedLeaves.
      const matchesConfirmedSelectionBranch =
        selectedItem.element === newPath[0] &&
        ((newPath.length === 1 && !selectedItem.subElement) ||
          (newPath.length === 2 && selectedItem.subElement === newPath[1]));
      if (matchesConfirmedSelectionBranch && newPath.length === (selectedItem.subElement ? 2 : 1)) {
        setLocalSelectedLeaves([...selectedItem.leaves]);
      } else {
        setLocalSelectedLeaves([]);
      }

      return newPath;
    });
  };
  const displayErrorMessage = required && hasOpened && internalError;
  const handleTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setStartY(e.touches[0].clientY);
    setCurrentHeight(height);
  };
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const deltaY = startY - e.touches[0].clientY;
    setHeight(Math.max(currentHeight + deltaY, initialHeight));
  };
  const handleTouchEnd = () => {
    if (height < (halfHeight + initialHeight) / 2) {
      setHeight(initialHeight);
    } else if (height < (maxHeight + halfHeight) / 2) {
      setHeight(halfHeight);
    } else {
      setHeight(maxHeight);
    }
  };
  const isLeafSelectionScreen = currentDisplayItems.every((item) => item.leaf !== undefined);
  return (
    <>
      <TextField
        label={label}
        value={getSelectedItemDisplay()}
        onClick={handleOpen}
        fullWidth
        error={displayErrorMessage || error}
        helperText={displayErrorMessage ? helperText || "Dette feltet er påkrevd" : " "}
        InputLabelProps={{ shrink: !!getSelectedItemDisplay() }}
        InputProps={{
          readOnly: true,
          endAdornment: <ArrowDropDownIcon />,
          style: {
            cursor: "pointer",
            fontStyle: !getSelectedItemDisplay() ? "italic" : "normal",
            color: !getSelectedItemDisplay() ? "#6F7979" : "#000",
          },
        }}
        sx={{
          "& .MuiInputLabel-root": {
            color: "#3F4949",
            fontFamily: "Inter",
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#00696D",
          },
          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            maxWidth: "calc(100% - 50px)",
          },
          "& .MuiInputBase-root": {
            padding: "0px",
          },
          "& .MuiInput-input": {
            padding: "16.5px 14px",
          },
        }}
        FormHelperTextProps={{
          sx: { height: '24px', margin: '0px' }
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          style: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "600px",
            position: "fixed",
            bottom: 0,
            margin: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#F2F4F4",
            height,
            boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.15)",
            height: bottomSheetHeight,
          },
        }}
        ref={dialogRef}
      >
        <Box
          onTouchStart={handleTouchStart}
          onTouchMove={handleDrag}
          onTouchEnd={handleTouchEnd}
          sx={{ textAlign: "center", padding: "15px 8px", cursor: "grab" }}
        >
          <Box sx={{ width: 32, height: 4, backgroundColor: "#6F7979", borderRadius: 2, margin: "0 auto" }} />
        </Box>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "4px 8px",
            fontWeight: 600,
            fontSize: "18px",
            color: "#3F4949",
            height: "64px",
            ...taskTemplateStyle.bottomSheetTitleStyle,
          }}
        >
          {path.length > 0 ? (
            <IconButton onClick={handleBack} sx={{ padding: "8px" }}>
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <IconButton onClick={handleClose} sx={{ padding: "8px" }}>
              <CloseIcon />
            </IconButton>
          )}
          {dialogTitle}
        </DialogTitle>
        <Box sx={{ padding: "0px 16px" }}><Divider /></Box>
        <DialogContent sx={{ overflowY: "auto", flexGrow: 1, padding: 0 }}>
          <List>
            {currentDisplayItems.map((item, index) => {
              const isLeafItem = item.leaf !== undefined;
              const hasChildren = item.elements?.length > 0 || item.leaves?.length > 0;
              return (
                <ListItem
                  key={`${path.join('-')}-${isLeafItem ? 'leaf-' + item.leaf : 'element-' + item.element}-${index}`}
                  button
                  onClick={() => handleItemClick(item)}
                  sx={{
                    height: 50,
                    px: 2,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    ...taskTemplateStyle.bottomSheetListItemStyle,
                  }}
                >
                  {isLeafItem && (
                    <ListItemIcon sx={{ paddingLeft: "16px", minWidth: "auto" }}>
                      <Checkbox
                        checked={localSelectedLeaves.includes(item.leaf)}
                        edge="start"
                        sx={{
                          color: "#6F7979",
                          "&.Mui-checked": {
                            color: "#00696D",
                            "& .MuiSvgIcon-root": {
                              color: "#00696D",
                            },
                          },
                        }}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={isLeafItem ? item.leaf : item.element} />
                  {!isLeafItem && hasChildren && (
                    <ListItemIcon sx={{ minWidth: "36px", marginLeft: "auto" }}>
                      <ArrowRightIcon fontSize="small" />
                    </ListItemIcon>
                  )}
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        {isLeafSelectionScreen && (
          <Button
            variant="contained"
            onClick={handleOk}
            disabled={localSelectedLeaves.length === 0}
            sx={{
              height: "40px",
              borderRadius: "100px",
              margin: "16px auto",
              px: 3,
              backgroundColor: "#00696D",
              color: "#fff",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#005B60",
              },
            }}
          >
            Ok
          </Button>
        )}
      </Dialog>
    </>
  );
};
export default BottomSheetNestedDropdown;
